import { Button, Flex, Modal, Typography } from '@iheartjane/reefer';
import dompurify from 'dompurify';

import { useHandleIframeModal } from '@jane/shared-ecomm/hooks';

import {
  StyledDiv,
  StyledLink,
  StyledModalContent,
} from './textCommsBanner.styles';

const SEGMENT_SEPARATOR = '  ·  ';

export const cleanBannerText = ({
  message,
  title,
}: {
  message?: string;
  title: string;
}) => {
  if (!message) return title;

  const tempElement = document.createElement('div');
  tempElement.innerHTML = message.replace(/<br\s*\/?>/gi, '\n');

  const blocks = tempElement.querySelectorAll('p, li');
  const rawSegments = blocks.length
    ? Array.from(blocks, (block) => block.textContent ?? '')
    : [tempElement.textContent ?? ''];

  const cleaned = rawSegments
    .flatMap((segment) => segment.split('\n'))
    .map((segment) => segment.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(SEGMENT_SEPARATOR);

  return `${title}: ${cleaned}`;
};

export interface TextCommsBannerProps {
  bannerText?: string;
  handleShowModal: (showModal: boolean) => void;
  isMobile?: boolean;
  message?: string | null;
  showModal: boolean;
  title?: string | null;
}

export const TextCommsBanner = ({
  bannerText,
  isMobile = false,
  handleShowModal,
  showModal,
  title,
  message,
}: TextCommsBannerProps) => {
  useHandleIframeModal(showModal);

  const formattedBannerText = !bannerText
    ? cleanBannerText({
        title: title || '',
        message: message || undefined,
      })
    : '';

  return (
    <>
      <Flex
        {...(isMobile && {
          ariaLabel: 'view-more',
          onClick: () => handleShowModal(true),
        })}
        justifyContent="space-between"
        data-testid="menu-comms-text-banner"
      >
        <StyledDiv
          dangerouslySetInnerHTML={{
            __html: dompurify.sanitize(bannerText || formattedBannerText),
          }}
        />

        {!isMobile && (
          <StyledLink
            ml={16}
            ariaLabel="view-more"
            branded
            onClick={() => handleShowModal(true)}
          >
            View more
          </StyledLink>
        )}
      </Flex>
      <Modal
        onRequestClose={() => handleShowModal(false)}
        open={showModal}
        variant="dialogue"
        overlayClose
      >
        <StyledModalContent>
          {title && (
            <Typography as="h1" branded variant="h2-semibold">
              {title}
            </Typography>
          )}

          {message && (
            <Typography mt={8} as="div" branded>
              <div
                dangerouslySetInnerHTML={{
                  __html: dompurify.sanitize(message),
                }}
              />
            </Typography>
          )}

          <Button
            full
            mt={24}
            onClick={() => handleShowModal(false)}
            label="Close"
          />
        </StyledModalContent>
      </Modal>
    </>
  );
};
